import React, { useEffect, useRef } from 'react';

const POINTS = 200;
const LINK_DISTANCE = 0.42; // in unit-sphere space

// Points spread evenly over a unit sphere (Fibonacci lattice).
const buildMesh = () => {
    const golden = Math.PI * (3 - Math.sqrt(5));
    const points = Array.from({ length: POINTS }, (_, i) => {
        const y = 1 - (i / (POINTS - 1)) * 2;
        const r = Math.sqrt(1 - y * y);
        const t = golden * i;
        return [Math.cos(t) * r, y, Math.sin(t) * r];
    });
    const edges = [];
    for (let a = 0; a < POINTS; a += 1) {
        for (let b = a + 1; b < POINTS; b += 1) {
            const dx = points[a][0] - points[b][0];
            const dy = points[a][1] - points[b][1];
            const dz = points[a][2] - points[b][2];
            if (dx * dx + dy * dy + dz * dz < LINK_DISTANCE * LINK_DISTANCE) edges.push([a, b]);
        }
    }
    return { points, edges };
};

const cssVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

// Decorative rotating 3D mesh drawn on a 2D canvas (no WebGL / extra
// dependencies). Pauses when off-screen or the tab is hidden, follows the
// pointer gently, and renders a single static frame for reduced motion.
function HeroScene({ className = '' }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas && canvas.getContext && canvas.getContext('2d');
        if (!ctx) return undefined;

        const { points, edges } = buildMesh();
        const reduceMotion = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        let width = 0;
        let height = 0;
        let frame = 0;
        let running = false;
        let angle = 0.6;
        let tiltX = 0.35;
        let targetX = 0.35;
        let targetY = 0;
        let pointerY = 0;
        let colors = { line: '#39414b', accent: '#e2603a' };

        const readColors = () => {
            colors = { line: cssVar('--line-strong') || colors.line, accent: cssVar('--accent') || colors.accent };
        };

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            width = rect.width;
            height = rect.height;
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const draw = () => {
            ctx.clearRect(0, 0, width, height);
            const radius = Math.min(width, height) * 0.46;
            const cx = width / 2;
            const cy = height / 2;
            const cosY = Math.cos(angle);
            const sinY = Math.sin(angle);
            const cosX = Math.cos(tiltX);
            const sinX = Math.sin(tiltX);

            const projected = points.map(([x, y, z]) => {
                const rx = x * cosY + z * sinY;
                const rz = -x * sinY + z * cosY;
                const ry = y * cosX - rz * sinX;
                const rz2 = y * sinX + rz * cosX;
                const scale = 1 / (1.9 - rz2 * 0.55);
                return [cx + rx * radius * scale * 1.9, cy + ry * radius * scale * 1.9, rz2];
            });

            ctx.lineWidth = 1;
            edges.forEach(([a, b]) => {
                const depth = (projected[a][2] + projected[b][2]) / 2; // -1 (back) .. 1 (front)
                ctx.globalAlpha = 0.08 + (depth + 1) * 0.22;
                ctx.strokeStyle = colors.line;
                ctx.beginPath();
                ctx.moveTo(projected[a][0], projected[a][1]);
                ctx.lineTo(projected[b][0], projected[b][1]);
                ctx.stroke();
            });

            projected.forEach(([px, py, depth], i) => {
                const accent = i % 9 === 0;
                ctx.globalAlpha = 0.25 + (depth + 1) * 0.35;
                ctx.fillStyle = accent ? colors.accent : colors.line;
                ctx.beginPath();
                ctx.arc(px, py, accent ? 2.4 : 1.4, 0, Math.PI * 2);
                ctx.fill();
            });
            ctx.globalAlpha = 1;
        };

        const tick = () => {
            angle += 0.0028 + pointerY * 0.002;
            tiltX += (targetX - tiltX) * 0.04;
            pointerY += (targetY - pointerY) * 0.04;
            draw();
            frame = requestAnimationFrame(tick);
        };

        const start = () => {
            if (running || reduceMotion) return;
            running = true;
            frame = requestAnimationFrame(tick);
        };
        const stop = () => {
            running = false;
            cancelAnimationFrame(frame);
        };

        const onPointer = (e) => {
            targetX = 0.35 + (e.clientY / window.innerHeight - 0.5) * 0.5;
            targetY = (e.clientX / window.innerWidth - 0.5) * 2;
        };
        const onVisibility = () => (document.hidden ? stop() : start());

        readColors();
        resize();
        draw();

        let observer;
        if ('IntersectionObserver' in window) {
            observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()));
            observer.observe(canvas);
        } else {
            start();
        }

        // Re-read palette when the theme attribute flips.
        const themeObserver = new MutationObserver(() => {
            readColors();
            if (!running) draw();
        });
        themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-accent'] });

        window.addEventListener('resize', resize);
        window.addEventListener('pointermove', onPointer, { passive: true });
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            stop();
            if (observer) observer.disconnect();
            themeObserver.disconnect();
            window.removeEventListener('resize', resize);
            window.removeEventListener('pointermove', onPointer);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    return <canvas ref={canvasRef} className={className} aria-hidden='true' />;
}

export default HeroScene;
